import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { ITranslationSyncLanguageSummary } from "../../../@types/admin/AdminMasterTranslations";
import { TableSorted } from "../../../components/TableSorted";
import { TableLoadingState, TableWrapper } from "../shared";
import {
  SummaryCountButton,
  SummaryCountZero,
} from "./AdminTranslations.styles";
import {
  ISummaryCellFilter,
  summaryColumnLabel,
  SummaryColumn,
} from "./syncState";

const COLUMNS: SummaryColumn[] = [
  "masterCount",
  "localCount",
  "inSync",
  "different",
  "missingLocally",
  "onlyInLocal",
  "missingVsEnglish",
  "archivedInMaster",
  "archivedInLocal",
];

interface ISyncSummaryTableProps {
  summary: ITranslationSyncLanguageSummary[];
  isLoading: boolean;
  environmentName: string;
  selected: ISummaryCellFilter | null;
  onSelect: (filter: ISummaryCellFilter) => void;
}

/**
 * Per-language counts of master vs this environment, above the key list of
 * the Translations sync page (EN-5330). Each non-zero count filters the key
 * list to the keys behind it.
 */
export function SyncSummaryTable({
  summary,
  isLoading,
  environmentName,
  selected,
  onSelect,
}: ISyncSummaryTableProps) {
  const columns: ColumnDef<ITranslationSyncLanguageSummary, number>[] = useMemo(
    () => [
      {
        accessorKey: "languageCode",
        header: "Language",
        enableSorting: true,
      },
      ...COLUMNS.map(
        (column): ColumnDef<ITranslationSyncLanguageSummary, number> => ({
          accessorKey: column,
          header: summaryColumnLabel(column, environmentName),
          cell: ({ row }) => {
            const { languageCode } = row.original;
            const count = row.original[column];
            if (count === 0) return <SummaryCountZero>0</SummaryCountZero>;

            const isSelected =
              selected?.languageCode === languageCode &&
              selected.column === column;
            return (
              <SummaryCountButton
                type="button"
                selected={isSelected}
                aria-pressed={isSelected}
                aria-label={`${languageCode}, ${summaryColumnLabel(
                  column,
                  environmentName
                )}: ${count}`}
                onClick={() => onSelect({ languageCode, column })}
              >
                {count}
              </SummaryCountButton>
            );
          },
          enableSorting: true,
        })
      ),
    ],
    [environmentName, selected, onSelect]
  );

  return (
    <TableWrapper refreshKeys={[["admin-translations-sync-summary"]]}>
      <TableSorted
        columns={columns}
        data={summary}
        emptyStateText={isLoading ? <TableLoadingState /> : "No translations"}
      />
    </TableWrapper>
  );
}
